import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Input,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import AssetIcon from "../../../components/elements/assetIcons";
import Autosuggest from "../../../components/elements/autosuggest";
import CheckBoxInput from "../../../components/elements/checkbox";
import FileSelectInput from "../../../components/elements/fileSelectInput";
import FormGroup from "../../../components/elements/formGroup";
import LocationSelectField from "../../../components/elements/locationSelectField";
import MultiSelect from "../../../components/elements/multiSelect";
import { getFloorPlanCreatedMessage } from "../../../constants/lang/en";
import {
  AssetCategoryFragmentFragment,
  useFloorPlanCreateMutation,
  useFloorPlanTypesQuery,
} from "../../../graphql/graphql";
import { setGenericMessage } from "../../../utils/serverErrors";
import {
  assetCategoryIdsSchema,
  createdBySchema,
  createdDateSchema,
  descriptionSchema,
  extractAllSheetsScheme,
  fileIdsSchema,
  fileIdsUploadingSchema,
  idSchema,
  nameSchema,
  typeSchema,
  yupObject,
} from "../../../utils/validation";

interface FloorPlanCreatePresenterProps {
  locationId?: string;
  assetCategories: AssetCategoryFragmentFragment[];
  handleClose?: () => void;
  goBack?: boolean;
}

const FloorPlanCreatePresenter: React.FC<FloorPlanCreatePresenterProps> = ({
  locationId,
  assetCategories,
  handleClose,
  goBack,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [floorPlanCreateMutation, { loading }] = useFloorPlanCreateMutation();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { data } = useFloorPlanTypesQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const onSubmit = async (data: FloorPlanFormData) => {
    try {
      await Promise.all(
        data.fileIds.map((fileId) =>
          floorPlanCreateMutation({
            variables: {
              data: {
                ...data,
                fileIds: undefined,
                fileId,
                fileIdsUploading: undefined,
              } as any,
            },
          })
        )
      );
      onDrawerClose();
      toast({
        description: getFloorPlanCreatedMessage(data.fileIds.length),
        status: "success",
        position: "top",
        isClosable: true,
      });
    } catch (error) {
      toast({
        description: setGenericMessage(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  };

  const onDrawerClose = () => {
    if (handleClose) handleClose();
    onClose();
    if (goBack) {
      setTimeout(() => {
        navigate(-1);
      }, 500);
    }
  };

  const assetCategoryItems = React.useMemo(
    () =>
      assetCategories.map((assetCategory) => ({
        value: assetCategory.id,
        label: (
          <Flex alignItems="center">
            <AssetIcon
              iconName={assetCategory.iconName}
              iconColor={assetCategory.iconColor}
              iconType={assetCategory.iconType}
              iconSize="xs"
            />
            <Box marginLeft="1">{assetCategory.name}</Box>
          </Flex>
        ),
        query: assetCategory.name,
      })),
    [assetCategories]
  );

  return (
    <>
      <Helmet>
        <title>Add New Plan</title>
      </Helmet>
      <Drawer isOpen={isOpen} placement="right" onClose={onDrawerClose}>
        <DrawerOverlay>
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Add New Plan</DrawerHeader>
            <DrawerBody>
              <Formik
                initialValues={{
                  name: "",
                  type: "",
                  assetCategoryIds: [] as string[],
                  description: "",
                  fileIds: [] as string[],
                  fileIdsUploading: false,
                  createdBy: "",
                  createdDate: "",
                  extractAllSheets: true,
                  locationId: locationId ?? "",
                }}
                validationSchema={planFormValidationSchema}
                onSubmit={onSubmit}
              >
                {({ getFieldProps }) => (
                  <Form id="floor_plan_create" noValidate>
                    {!locationId && (
                      <LocationSelectField
                        name="locationId"
                        multiSelect={false}
                        autoFocus
                      />
                    )}
                    <FormGroup
                      label="Name"
                      name="name"
                      hint="To use name from uploaded file, leave this field blank"
                    >
                      <Input
                        autoFocus={!!locationId}
                        autoComplete="off"
                        {...getFieldProps("name")}
                      />
                    </FormGroup>
                    <FormGroup label="Type" name="type">
                      <Autosuggest
                        items={data?.floorPlanTypes || []}
                        autoComplete="off"
                        name="type"
                      />
                    </FormGroup>
                    <MultiSelect
                      sortOptions
                      items={assetCategoryItems}
                      autoComplete="off"
                      name="assetCategoryIds"
                      label="Categories"
                    />
                    <FormGroup label="Description" name="description">
                      <Textarea
                        autoComplete="off"
                        {...getFieldProps("description")}
                      />
                    </FormGroup>
                    <FileSelectInput name="fileIds" mapUpload />
                    <CheckBoxInput
                      name="extractAllSheets"
                      label={
                        <Flex alignItems="center">
                          <Box marginRight="2">
                            Create a separate PDF file for each sheet
                          </Box>
                          <Tooltip
                            label="Check this option if you want each page of your multi-page document to be extracted into separate files"
                            hasArrow
                          >
                            <Box color="gray.600">
                              <FontAwesomeIcon icon={faQuestionCircle} />
                            </Box>
                          </Tooltip>
                        </Flex>
                      }
                    />
                  </Form>
                )}
              </Formik>
            </DrawerBody>
            <DrawerFooter flexDirection="column">
              <Button
                width="full"
                type="submit"
                isLoading={loading}
                form="floor_plan_create"
              >
                Save
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
};

export default FloorPlanCreatePresenter;

gql`
  mutation FloorPlanCreate($data: floorPlanInput!) {
    floorPlanCreate(data: $data)
  }
`;

const planFormValidationSchema = yupObject().shape({
  locationId: idSchema.label("Location"),
  name: nameSchema.notRequired().label("Plan name"),
  description: descriptionSchema,
  fileIds: fileIdsSchema.min(1, "Please add plan to continue"),
  fileIdsUploading: fileIdsUploadingSchema,
  type: typeSchema.optional(),
  createdBy: createdBySchema,
  createdDate: createdDateSchema,
  assetCategoryIds: assetCategoryIdsSchema,
  extractAllSheets: extractAllSheetsScheme,
});

type FloorPlanFormData = {
  locationId: string;
  name: string;
  description: string;
  fileIds: string[];
  fileIdsUploading: boolean;
  type: string;
  createdBy: string;
  createdDate: string;
  assetCategoryIds: string[];
  extractAllSheets: boolean;
};
